<template>
  <div class="notification-dropdown">
    <a-dropdown 
      :trigger="['click']" 
      placement="bottomRight"
      overlayClassName="header-notifications-dropdown"
      @visibleChange="handleDropdownChange"
    >
      <a-badge :count="unreadCount">
        <a class="ant-dropdown-link" @click="e => e.preventDefault()">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 2C6.68632 2 4.00003 4.68629 4.00003 8V11.5858L3.29292 12.2929C3.00692 12.5789 2.92137 13.009 3.07615 13.3827C3.23093 13.7564 3.59557 14 4.00003 14H16C16.4045 14 16.7691 13.7564 16.9239 13.3827C17.0787 13.009 16.9931 12.5789 16.7071 12.2929L16 11.5858V8C16 4.68629 13.3137 2 10 2Z" fill="#111827"/>
            <path d="M10 18C8.34315 18 7 16.6569 7 15H13C13 16.6569 11.6569 18 10 18Z" fill="#111827"/>
          </svg>
        </a>
      </a-badge>
      
      <template #overlay>
        <div class="notification-panel">
          <div class="notification-header">
            <h6 class="notification-title">Notifications</h6>
            <div class="notification-actions">
              <a-button 
                type="link" 
                size="small" 
                @click.stop="markAllAsRead"
                :disabled="unreadCount === 0"
              >
                Mark all read
              </a-button>
            </div>
          </div>
          
          <div class="notification-content">
            <a-spin :spinning="loading" size="small">
              <div v-if="notifications.length === 0 && !loading" class="no-notifications">
                <a-empty 
                  description="No notifications"
                  :image-style="{ height: '60px' }"
                />
              </div>
              
              <div v-else class="notification-list">
                <div 
                  v-for="notification in notifications" 
                  :key="notification.id"
                  class="notification-item"
                  :class="{ 
                    'unread': !notification.is_read,
                    'bid_created': notification.type === 'bid_created',
                    'bid_updated': notification.type === 'bid_updated',
                    'bid_cancelled': notification.type === 'bid_cancelled'
                  }"
                  @click.stop="markAsRead(notification)"
                >
                  <div class="notification-icon">
                    <a-icon 
                      :type="getNotificationIcon(notification.type)" 
                      :style="{ color: getNotificationColor(notification.type) }"
                    />
                  </div>
                  
                  <div class="notification-body">
                    <div class="notification-message">
                      <strong>{{ notification.title }}</strong>
                      <p>{{ notification.message }}</p>
                    </div>
                    
                    <div class="notification-meta">
                      <span class="notification-time">
                        {{ formatTime(notification.created_at) }}
                      </span>
                      <span v-if="notification.load" class="notification-load">
                        Load: {{ notification.load.external_load_id }}
                      </span>
                    </div>
                  </div>
                  
                  <div v-if="!notification.is_read" class="notification-dot"></div>
                </div>
                
                <!-- Load More Button -->
                <div v-if="hasMore" class="load-more-container">
                  <a-button 
                    type="link" 
                    size="small" 
                    :loading="loadingMore"
                    @click.stop="loadMoreNotifications"
                    class="load-more-btn"
                  >
                    <a-icon type="down" />
                    Load More
                  </a-button>
                </div>
              </div>
            </a-spin>
          </div>
        </div>
      </template>
    </a-dropdown>
  </div>
</template>

<script>
import { notificationAPI } from '@/api/notification';
import moment from 'moment';

export default {
  name: 'NotificationDropdown',
  data() {
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      loadingMore: false,
      hasMore: true,
      offset: 0,
      limit: 10
    };
  },
  mounted() {
    this.loadNotifications();
    this.loadUnreadCount();
    
    // Set up WebSocket listener for real-time notifications
    this.setupWebSocketListener();
  },
  beforeDestroy() {
    // Clean up WebSocket listener
    if (this.$wsOff) {
      this.$wsOff('new_notification', this.handleNewNotification);
      this.$wsOff('notification_read', this.handleNotificationRead);
      this.$wsOff('connected', this.handleWebSocketConnected);
    }
  },
  methods: {
    async loadNotifications(reset = true) {
      if (reset) {
        this.loading = true;
        this.offset = 0;
        this.hasMore = true;
      } else {
        this.loadingMore = true;
      }
      
      try {
        const response = await notificationAPI.getNotifications({ 
          limit: this.limit, 
          offset: this.offset 
        });
        const newNotifications = response.data.notifications || [];
        
        if (reset) {
          this.notifications = newNotifications;
        } else {
          this.notifications = [...this.notifications, ...newNotifications];
        }
        
        // Check if there are more notifications
        this.hasMore = newNotifications.length === this.limit;
        this.offset += this.limit;
        
      } catch (error) {
        console.error('Error loading notifications:', error);
        this.$message.error('Failed to load notifications');
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    
    async loadUnreadCount() {
      try {
        const response = await notificationAPI.getUnreadCount();
        this.unreadCount = response.data.count || 0;
      } catch (error) {
        console.error('Error loading unread count:', error);
      }
    },
    
    async loadMoreNotifications() {
      if (!this.hasMore || this.loadingMore) {
        return;
      }
      await this.loadNotifications(false);
    },
    
    async markAsRead(notification) {
      if (notification.is_read) return;
      
      try {
        await notificationAPI.markAsRead(notification.id);
        notification.is_read = true;
        notification.read_at = new Date().toISOString();
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      } catch (error) {
        console.error('Error marking notification as read:', error);
        this.$message.error('Failed to mark notification as read');
      }
    },
    
    async markAllAsRead() {
      try {
        await notificationAPI.markAllAsRead();
        this.notifications.forEach(notification => {
          notification.is_read = true;
          notification.read_at = new Date().toISOString();
        });
        this.unreadCount = 0;
        this.$message.success('All notifications marked as read');
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
        this.$message.error('Failed to mark all notifications as read');
      }
    },
    
    
    handleDropdownChange(visible) {
      if (visible) {
        this.loadNotifications(true); // Reset pagination when dropdown opens
      }
    },
    
    getNotificationIcon(type) {
      const icons = {
        'bid_created': 'plus-circle',
        'bid_updated': 'edit',
        'bid_cancelled': 'close-circle'
      };
      return icons[type] || 'bell';
    },
    
    getNotificationColor(type) {
      const colors = {
        'bid_created': '#52c41a',
        'bid_updated': '#1890ff',
        'bid_cancelled': '#ff4d4f'
      };
      return colors[type] || '#666';
    },
    
    formatTime(timestamp) {
      return moment(timestamp).fromNow();
    },
    
    setupWebSocketListener() {
      if (this.$wsOn) {
        this.$wsOn('new_notification', this.handleNewNotification);
        this.$wsOn('notification_read', this.handleNotificationRead);
        this.$wsOn('connected', this.handleWebSocketConnected);
      }
    },
    
    handleWebSocketConnected() {
      // Refresh unread count when WebSocket reconnects to ensure accuracy
      this.loadUnreadCount();
    },
    
    handleNewNotification(notification) {
      // Add new notification to the top of the list
      this.notifications.unshift(notification);
      
      // Keep only the latest 10 notifications
      if (this.notifications.length > 10) {
        this.notifications = this.notifications.slice(0, 10);
      }
      
      // Increment unread count
      this.unreadCount++;
      
      // Show browser notification if permission is granted
      this.showBrowserNotification(notification);
    },
    
    handleNotificationRead(data) {
      // Update unread count when notifications are marked as read
      if (data.all_read) {
        this.unreadCount = 0;
      } else if (data.notification_id) {
        // Find and update the specific notification
        const notification = this.notifications.find(n => n.id === data.notification_id);
        if (notification) {
          notification.is_read = true;
          notification.read_at = new Date().toISOString();
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      }
    },
    
    showBrowserNotification(notification) {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification.id
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.notification-dropdown {
  .ant-dropdown-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.04);
      border-radius: 4px;
    }
    
    svg {
      transition: all 0.3s ease;
    }
    
    &:hover svg {
      transform: scale(1.05);
    }
  }
}

// Global styles for the dropdown overlay
:global(.header-notifications-dropdown) {
  .ant-dropdown-menu {
    padding: 0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: none;
  }
}

.notification-panel {
  width: 350px;
  max-height: 500px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #f0f0f0;
  
  .notification-header {
    padding: 16px 20px 12px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .notification-title {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #111827;
    }
    
    .notification-actions {
      .ant-btn-link {
        padding: 0;
        height: auto;
        font-size: 12px;
      }
    }
  }
  
  .notification-content {
    max-height: 350px;
    overflow-y: auto;
    
    .no-notifications {
      padding: 40px 20px;
      text-align: center;
    }
    
    .notification-list {
      .notification-item {
        padding: 12px 20px;
        border-bottom: 1px solid #f0f0f0;
        cursor: pointer;
        display: flex;
        align-items: flex-start;
        transition: background-color 0.2s;
        
        &:hover {
          background-color: #f8f9fa;
        }
        
        &.unread {
          &.bid_created {
            background-color: #f6ffed;
            border-left: 3px solid #52c41a;
          }
          
          &.bid_updated {
            background-color: #fff7e6;
            border-left: 3px solid #fa8c16;
          }
          
          &.bid_cancelled {
            background-color: #fff2f0;
            border-left: 3px solid #ff4d4f;
          }
          
          &:not(.bid_created):not(.bid_updated):not(.bid_cancelled) {
            background-color: #f0f8ff;
            border-left: 3px solid #1890ff;
          }
        }
        
        .notification-icon {
          margin-right: 12px;
          margin-top: 2px;
          font-size: 16px;
        }
        
        .notification-body {
          flex: 1;
          
          .notification-message {
            margin-bottom: 4px;
            
            strong {
              display: block;
              font-size: 14px;
              color: #111827;
              margin-bottom: 2px;
            }
            
            p {
              margin: 0;
              font-size: 13px;
              color: #666;
              line-height: 1.4;
            }
          }
          
          .notification-meta {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 11px;
            color: #999;
            
            .notification-time {
              font-weight: 500;
            }
            
            .notification-load {
              background: #f0f0f0;
              padding: 2px 6px;
              border-radius: 4px;
              font-weight: 500;
            }
          }
        }
        
        .notification-dot {
          width: 8px;
          height: 8px;
          background: #1890ff;
          border-radius: 50%;
          margin-left: 8px;
          margin-top: 6px;
        }
      }
    }
  }
}

// Custom scrollbar for notification list
.notification-content .notification-list::-webkit-scrollbar {
  width: 4px;
}

.notification-content .notification-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.notification-content .notification-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.notification-content .notification-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// Load More Button Styles
.load-more-container {
  padding: 12px 20px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
  
  .load-more-btn {
    color: #1890ff;
    font-size: 12px;
    padding: 4px 8px;
    height: auto;
    
    &:hover {
      color: #40a9ff;
    }
    
    .anticon {
      margin-right: 4px;
    }
  }
}
</style>
